/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import BigNumberJS from 'bignumber.js'

import { GEODE_URL } from '../constants'
import {
  DonationResultQueryData,
  PaymentType,
  TripleAPaymentMutationBody,
  XfersPaymentMutationBody
} from '../types/api'
import { CouldBe, Nullable } from '../types/main'

/*type TxRecord = {
  time: number
  txId: string
}*/

/*type PaymentHistory = {
  [account: string]: CouldBe<TxRecord[]>
}*/

/**
 * [putDonationAmount description]
 * @type {[type]}
 */
export const setLocalStorageDonationAmount = (amount: string) => {
  return localStorage.setItem('donation', amount)
}

/**
 * [getDonationAmount description]
 * @type {[type]}
 */
export const getLocalStorageDonationAmount = () => {
  const storageData = localStorage.getItem('donation') || null

  let donationAmount: Nullable<number> = null
  if (typeof storageData === 'string') {
    donationAmount = new BigNumberJS(storageData.replaceAll(',', '')).toNumber()
  }

  return donationAmount
}

/**
 * [deleteDonationAmount description]
 * @type {[type]}
 */
export const removeLocalStorageDonationAmount = () => {
  return localStorage.removeItem('donation')
}

/**
 * [setLocalStorageDonationHistoryHash description]
 * @type {[type]}
 */
export const setLocalStorageDonationHistoryHash = (hash: string) => {
  return localStorage.setItem('historyHash', hash)
}

/**
 * [getLocalStorageDonationHistoryHash description]
 * @type {[type]}
 */
export const getLocalStorageDonationHistoryHash = () => {
  return localStorage.getItem('historyHash')
}

/**
 * [setLocalStorageDonationCouponClaimSetting description]
 * @type {[type]}
 */
export const setLocalStorageDonationWillClaimCoupon = (willClaimCoupon: boolean) => {
  return localStorage.setItem('willClaimCoupon', willClaimCoupon ? '1' : '0')
}

/**
 * [getLocalStorageDonationWillClaimCoupon description]
 * @type {[type]}
 */
export const getLocalStorageDonationWillClaimCoupon = () => {
  return localStorage.getItem('willClaimCoupon')
}

/**
 * [setLocalStoragePaymentIds description]
 * @type {[type]}
 */
export const setLocalStorageNonRedemptionPaymentIds = (paymentId: string) => {
  const data = getLocalStorageNonRedemptionPaymentIds()
  data.push(paymentId)
  return localStorage.setItem('nonRedemptionPaymentIds', data.join(','))
}

/**
 * [getLocalStoragePaymentIds description]
 * @type {[type]}
 */
export const getLocalStorageNonRedemptionPaymentIds = () => {
  const data = localStorage.getItem('nonRedemptionPaymentIds')
  return !data ? ([] as string[]) : data.split(',').filter((id) => !!id)
}

/**
 * [async description]
 * @param {PaymentType} paymentType [description]
 * @param {PaymentMutationRequest} message [description]
 */
export const postXfersPaymentRequest = async (
  paymentType: PaymentType,
  requestBody: XfersPaymentMutationBody
) => {
  const path = paymentType === 'qr' ? 'qr' : 'bank-transfer'
  const url = `${GEODE_URL}/${path}`

  const request = new Request(url, {
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'POST'
  })

  return fetch(request)
}

/**
 * [async description]
 * @param {TripleAPaymentMutationBody} requestBody [description]
 */
export const postTripleAPaymentRequest = async (requestBody: TripleAPaymentMutationBody) => {
  const request = new Request(`${GEODE_URL}/cryptocurrency`, {
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'POST'
  })

  return fetch(request)
}

/**
 * [async description]
 * @param {string} paymentId [description]
 */
export const getDonationResultById = async (paymentId: string) => {
  const url = `${GEODE_URL}/donation-result/${paymentId}`
  const request = new Request(url, {
    headers: {
      'Content-Type': 'application/json'
    }
  })

  try {
    const response = await fetch(request)
    const data = (await response.json()) as DonationResultQueryData

    return data
  } catch (e: any) {
    throw new Error(e.message, {
      cause: e
    })
  }
}

/**
 * [async description]
 * @param {string} account [description]
 */
export const getDonationHistory = async (account: CouldBe<Nullable<string>>) => {
  const url = `${GEODE_URL}/donation-history/${account}`
  const request = new Request(url, {
    headers: {
      'Content-Type': 'application/json'
    }
  })

  const response = await fetch(request)
  const data = (await response.json()) as DonationResultQueryData[]

  return data
}

/**
 * [async description]
 * @param {string} account [description]
 */
export const batchFetchHistory = async (ids: string[]) => {
  const url = `${GEODE_URL}/donation-result-batch`
  const request = new Request(url, {
    body: JSON.stringify(ids),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  const response = await fetch(request)
  const data = (await response.json()) as DonationResultQueryData[]

  return data
}
